import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useMutation } from 'react-query'
import { AxiosError } from 'axios'

import {
  Edit as EditIcon,
  Home as HomeIcon,
  Print as PrintIcon,
  SyncAlt as SyncAltIcon,
} from '@material-ui/icons'

import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'
import { OrderLabel } from 'src/modules/supply_chain/components/parcel/OrderLabel'
import { PickTicket } from 'src/modules/supply_chain/components/parcel/PickTicket'
import { ParcelOrderDialog } from 'src/modules/supply_chain/components/parcel/OrderDialog'
import { ParcelOrder } from 'src/api/models'
import { handleErrorMessage, Notification } from 'src/components/Notification'
import {
  AddressField,
  DateField,
  DateTimeField,
  FlagsField,
  OrderStatusField,
  RelatedLinkedField,
  RelatedListValueField,
  ValueField,
} from 'src/components/Table'
import { skuit } from 'src/services/skuit'
import { DetailComponent } from './base'

interface ObjInfo {
  data: any
  isLoading: boolean
  refetch: () => void
}

interface DetailTableProps {
  obj: ObjInfo
  refresh?: () => void
  customFields?: Record<string, string>
}

export const ParcelOrderDetailTable = (props: DetailTableProps) => {
  const { obj, refresh, customFields } = props
  const [updateOpen, setUpdateOpen] = useState(false)
  const { print: asyncPrint, renderPrintComponent: renderAsyncPrintComponent } = useAsyncPrint()

  const { mutate: syncOrderTo } = useMutation(skuit.sc.integrations.shipstation.parcel.orders.syncTo, {
    onSuccess: () => {
      toast.success(() => (
        <Notification message={'Successfully synced Parcel Order to Shipstation'} />
      ))
    },
    onError: (error: AxiosError) => {
      handleErrorMessage(error, 'sync', 'Parcel Order')
    },
  })

  const { mutate: syncOrderFrom } = useMutation(skuit.sc.integrations.shipstation.parcel.orders.syncFrom, {
    onSuccess: () => {
      toast.success(() => (
        <Notification message={'Successfully synced Parcel Order to Shipstation'} />
      ))
    },
    onError: (error: AxiosError) => {
      handleErrorMessage(error, 'sync', 'Parcel Order')
    },
  })

  return (
    <>
      {renderAsyncPrintComponent()}
      {updateOpen && (
        <ParcelOrderDialog
          open={updateOpen}
          order={obj.data}
          update
          handleClose={() => {
            setUpdateOpen(false)
          }}
          onSave={refresh}
        />
      )}
      <DetailComponent
        obj={obj}
        Icon={HomeIcon}
        menuItems={[
          {
            Icon: EditIcon,
            actions: [{ title: 'Edit Parcel Order', onClick: () => setUpdateOpen(true) }],
          },
          {
            Icon: PrintIcon,
            actions: [
              {
                title: 'Print Parcel Order',
                onClick: () =>
                  asyncPrint((props) => <OrderLabel {...props} order={obj.data as ParcelOrder} />),
              },
              {
                title: 'Print Pick Ticket',
                onClick: () =>
                  asyncPrint((props) => <PickTicket {...props} order={obj.data as ParcelOrder} />),
              },
              {
                title: 'Print Parcel Order & Pick Ticket',
                onClick: () =>
                  asyncPrint((props) => (
                    <>
                      <OrderLabel order={obj.data as ParcelOrder} />
                      <PickTicket {...props} order={obj.data as ParcelOrder} />
                    </>
                  )),
              },
              {
                title: 'Print Parcel Order Placard',
                onClick: () =>
                  asyncPrint((props) => <OrderLabel {...props} order={obj.data as ParcelOrder} />),
              },
            ],
          },
          {
            Icon: SyncAltIcon,
            actions: [
              {
                title: 'Push Order Updates to Shipstation',
                onClick: () => syncOrderTo(obj.data?.guid || ''),
              },
              {
                title: 'Pull Order Updates from Shipstation',
                onClick: () => syncOrderFrom(obj.data?.guid || ''),
              },
            ],
          },
        ]}
        fields={[
          { id: 'guid', label: 'SquID', renderer: ValueField },
          { id: 'number', label: 'Number', renderer: ValueField },
          { id: 'client', label: 'Client', renderer: RelatedLinkedField(ValueField, 'name') },
          { id: 'facility', label: 'Facility', renderer: RelatedLinkedField(ValueField, 'name') },
          { id: 'carrier', label: 'Carrier', renderer: RelatedLinkedField(ValueField, 'name') },
          { id: 'placedOn', label: 'Date Placed', renderer: DateTimeField },
          { id: 'fulfillOn', label: 'Date to Fulfill On', renderer: DateField },
          { id: 'details', label: 'Details', renderer: ValueField },
          { id: 'address', label: 'Address', renderer: AddressField },
          { id: 'status', label: 'Status', renderer: OrderStatusField },
          { id: 'flags', label: 'Flags', renderer: FlagsField },
          {
            id: 'facilities',
            label: 'Assigned Facilities',
            renderer: RelatedListValueField('name'),
          },
        ]}
        customFields={customFields}
      />
    </>
  )
}
